// /frontend/src/pages/legal/PrivacyPolicy.jsx
import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Lock, Eye, Database, UserCheck, Mail, AlertTriangle, Bell, Trash2, Users, RefreshCw } from "lucide-react";

export default function PrivacyPolicy() {
  const lastUpdated = "April 7, 2026";

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#172A3A] via-[#1e3a4c] to-[#172A3A]">
      {/* Header */}
      <header className="bg-[#172A3A]/80 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white hover:text-[#FFB800] transition">
            <ArrowLeft size={20} />
            <span className="font-semibold">Back to Home</span>
          </Link>
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="Pro IRP" className="w-8 h-8 rounded-full bg-white" />
            <span className="text-xl font-bold text-white">Pro <span className="text-[#FFB800]">IRP</span></span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12">
          {/* Title */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#172A3A] rounded-full mb-4">
              <Shield className="w-8 h-8 text-[#FFB800]" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#172A3A] mb-2">Privacy Policy</h1>
            <p className="text-gray-500">Last Updated: {lastUpdated}</p>
          </div>

          {/* Introduction */}
          <section className="mb-10">
            <p className="text-gray-700 leading-relaxed">
              Pro IRP ("we," "our," or "us") is committed to protecting your privacy and the privacy of your clients.
              This Privacy Policy explains how we collect, use, disclose, and safeguard information when you use our
              insurance agent customer relationship management platform ("Service"). As a platform handling Protected
              Health Information (PHI), we are fully compliant with the Health Insurance Portability and Accountability
              Act (HIPAA) and other applicable privacy regulations.
            </p>
          </section>

          {/* Blue Button Attribution - REQUIRED BY CMS */}
          <section className="mb-10 bg-blue-50 rounded-xl p-6 border border-blue-200">
            <div className="flex items-start gap-4">
              <Shield className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
              <div>
                <h2 className="text-xl font-bold text-[#172A3A] mb-2">CMS Blue Button 2.0 Integration Notice</h2>
                <p className="text-gray-700 font-medium">
                  This product uses the Blue Button 2.0 APIs but is not endorsed or certified by the Centers for
                  Medicare & Medicaid Services or the U.S. Department of Health and Human Services.
                </p>
              </div>
            </div>
          </section>

          {/* Google Calendar Integration */}
          <section className="mb-10 bg-yellow-50 rounded-xl p-6 border border-yellow-200">
            <div className="flex items-start gap-4">
              <UserCheck className="w-6 h-6 text-yellow-600 mt-1 flex-shrink-0" />
              <div>
                <h2 className="text-xl font-bold text-[#172A3A] mb-2">Google Calendar Integration</h2>
                <p className="text-gray-700 mb-4">
                  Pro IRP offers an optional integration with Google Calendar to allow insurance agents to schedule
                  and manage client appointments directly from within the platform. This section explains exactly
                  how we handle data obtained through Google APIs.
                </p>

                <h3 className="text-lg font-semibold text-[#172A3A] mt-4 mb-2">What We Access</h3>
                <p className="text-gray-700 mb-2">
                  When you connect your Google Calendar, we request only the minimum scopes necessary:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mb-4">
                  <li><strong>Calendar Events (read/write):</strong> To display your existing appointments and create new appointment events on your behalf.</li>
                  <li><strong>Calendar List (read-only):</strong> To let you choose which of your Google Calendars to sync with Pro IRP.</li>
                </ul>
                <p className="text-gray-700 mb-4">
                  We do not request access to Gmail, Google Drive, Google Contacts, or any other Google services.
                </p>

                <h3 className="text-lg font-semibold text-[#172A3A] mt-4 mb-2">How We Use Google Calendar Data</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mb-4">
                  <li>To display your calendar appointments within the Pro IRP scheduling interface.</li>
                  <li>To create calendar events when you schedule client appointments through the platform.</li>
                  <li>Calendar data is used solely to provide the scheduling feature you requested — it is never used for advertising, profiling, or any purpose unrelated to scheduling.</li>
                </ul>

                <h3 className="text-lg font-semibold text-[#172A3A] mt-4 mb-2">Google Limited Use Policy</h3>
                <p className="text-gray-700 mb-2">
                  Pro IRP's use of data received from Google APIs adheres to the{" "}
                  <a
                    href="https://developers.google.com/terms/api-services-user-data-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#FFB800] hover:underline"
                  >
                    Google API Services User Data Policy
                  </a>
                  , including the Limited Use requirements. Specifically:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mb-4">
                  <li>Google Calendar data is used only to provide the in-app scheduling functionality described above.</li>
                  <li>We do not transfer, sell, or share Google Calendar data with any third parties, except as necessary to provide the scheduling service (e.g., encrypted storage on AWS infrastructure under BAA).</li>
                  <li>We do not use Google Calendar data for serving advertisements or for any ad-targeting purpose.</li>
                  <li>We do not allow humans to read your Google Calendar data unless you have given explicit permission, it is necessary for security purposes (e.g., investigating a security incident), or we are required to do so by law.</li>
                </ul>

                <h3 className="text-lg font-semibold text-[#172A3A] mt-4 mb-2">Storage and Retention</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mb-4">
                  <li>OAuth access and refresh tokens are encrypted at rest using AES-256 and stored in our AWS-hosted database.</li>
                  <li>Calendar event data is not persisted to our database — it is fetched live from Google's API each time you view the calendar.</li>
                  <li>If you disconnect Google Calendar from Settings, your stored OAuth tokens are deleted immediately.</li>
                </ul>

                <h3 className="text-lg font-semibold text-[#172A3A] mt-4 mb-2">Revoking Access</h3>
                <p className="text-gray-700">
                  You can disconnect your Google Calendar at any time from <strong>Settings → Integrations → Disconnect Google Calendar</strong>.
                  You may also revoke access directly through your{" "}
                  <a
                    href="https://myaccount.google.com/permissions"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#FFB800] hover:underline"
                  >
                    Google Account permissions page
                  </a>
                  . Upon revocation, we will delete all stored OAuth tokens within 24 hours.
                </p>
              </div>
            </div>
          </section>

          {/* HIPAA Compliance */}
          <section className="mb-10 bg-green-50 rounded-xl p-6 border border-green-100">
            <div className="flex items-start gap-4">
              <Lock className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
              <div>
                <h2 className="text-xl font-bold text-[#172A3A] mb-2">HIPAA Compliance</h2>
                <p className="text-gray-700">
                  Pro IRP is designed to be HIPAA-compliant. We implement administrative, physical, and technical
                  safeguards to protect the confidentiality, integrity, and availability of Protected Health Information
                  (PHI). We enter into Business Associate Agreements (BAAs) with covered entities as required by law.
                  We also comply with the Privacy Act of 1974 and all applicable federal and state laws regarding
                  the protection and disclosure of health information.
                </p>
              </div>
            </div>
          </section>

          {/* Information We Collect */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <Database className="w-6 h-6 text-[#FFB800]" />
              <h2 className="text-2xl font-bold text-[#172A3A]">Information We Collect</h2>
            </div>

            <h3 className="text-lg font-semibold text-[#172A3A] mt-6 mb-3">Account Information</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Name, email address, and phone number</li>
              <li>Agency/organization affiliation</li>
              <li>National Producer Number (NPN) and licensing information</li>
              <li>Login credentials (passwords are encrypted and never stored in plain text)</li>
            </ul>

            <h3 className="text-lg font-semibold text-[#172A3A] mt-6 mb-3">Client Information (PHI)</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Client names, contact information, and demographics</li>
              <li>Medicare Beneficiary Identifiers (MBI)</li>
              <li>Health insurance plan information</li>
              <li>Prescription drug information (via CMS Blue Button 2.0 integration)</li>
              <li>Medicare Parts A, B, and D claims data</li>
              <li>Coverage and enrollment information</li>
              <li>Communication records between agents and clients</li>
            </ul>

            <h3 className="text-lg font-semibold text-[#172A3A] mt-6 mb-3">Google Calendar Data (Optional)</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Calendar event metadata (titles, dates, times) fetched live from Google's API when you view the calendar — not stored in our database</li>
              <li>List of your Google Calendars (read-only) to allow calendar selection</li>
              <li>OAuth access and refresh tokens (encrypted) to maintain your authorized connection</li>
            </ul>

            <h3 className="text-lg font-semibold text-[#172A3A] mt-6 mb-3">Usage Information</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Log data (IP addresses, browser type, pages visited)</li>
              <li>Device information</li>
              <li>Feature usage and interaction data</li>
            </ul>
          </section>

          {/* How We Use Information */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <Eye className="w-6 h-6 text-[#FFB800]" />
              <h2 className="text-2xl font-bold text-[#172A3A]">How We Use Your Information</h2>
            </div>
            <p className="text-gray-700 mb-4">
              We use the information we collect only for the specific purposes described below and as specified
              at the time of data collection:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>To provide and maintain our Service</li>
              <li>To facilitate insurance agent-client relationships</li>
              <li>To retrieve Medicare beneficiary data through CMS Blue Button 2.0 API (with explicit beneficiary consent)</li>
              <li>To send automated communications on behalf of agents (appointment reminders, policy updates)</li>
              <li>To generate analytics and insights to help agents better serve their clients</li>
              <li>To detect, prevent, and address technical issues or security threats</li>
              <li>To comply with legal obligations and regulatory requirements</li>
              <li>To maintain data accuracy, relevance, timeliness, and completeness</li>
            </ul>
          </section>

          {/* Blue Button 2.0 Detailed Section */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <UserCheck className="w-6 h-6 text-[#FFB800]" />
              <h2 className="text-2xl font-bold text-[#172A3A]">CMS Blue Button 2.0 Medicare Data</h2>
            </div>
            <p className="text-gray-700 mb-4">
              Our Service integrates with the Centers for Medicare & Medicaid Services (CMS) Blue Button 2.0 API
              to retrieve Medicare beneficiary data. This section explains how we handle this sensitive health information:
            </p>

            <h3 className="text-lg font-semibold text-[#172A3A] mt-6 mb-3">Consent and Authorization</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Explicit consent required:</strong> Medicare beneficiaries must explicitly authorize access to their data through Medicare.gov. We never request or access Medicare.gov login credentials.</li>
              <li><strong>Active opt-in:</strong> Users must actively agree to our privacy policy and terms of service before authorization - we do not default to agreement.</li>
              <li><strong>Clear explanation:</strong> Before authorization, we clearly explain what data will be collected, how it will be used, and with whom it may be shared.</li>
            </ul>

            <h3 className="text-lg font-semibold text-[#172A3A] mt-6 mb-3">Data Collection Scope</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>What we access:</strong> Medicare Parts A, B, and D claims data, coverage information, and beneficiary demographics</li>
              <li><strong>When we collect data:</strong> We collect your Medicare data once when you first give permission. After that, your insurance agent can refresh the data when needed to help you - for example, before your Annual Enrollment Period. We only get new data when your agent clicks "Sync" in our system.</li>
              <li><strong>How long we keep it:</strong> We keep your Medicare data only while you allow us to. If you take away permission, we delete your Medicare data within 30 days.</li>
            </ul>

            <h3 className="text-lg font-semibold text-[#172A3A] mt-6 mb-3">Third-Party Sharing</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>No sharing without consent:</strong> We do not disclose any Medicare beneficiary information to third parties without specific, explicit consent from the beneficiary</li>
              <li><strong>No sale of data:</strong> We never sell Medicare beneficiary data to any third party</li>
              <li><strong>Service providers:</strong> Our infrastructure providers (AWS) who process data on our behalf are bound by Business Associate Agreements and meet HIPAA security requirements</li>
            </ul>

            <h3 className="text-lg font-semibold text-[#172A3A] mt-6 mb-3">Revoking Access</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Revocation right:</strong> Beneficiaries can revoke access at any time through their insurance agent or by contacting us directly</li>
              <li><strong>Data deletion:</strong> When access is revoked, we will delete all Medicare data associated with that beneficiary within 30 days, unless retention is required for legal or regulatory purposes</li>
              <li><strong>Previously shared data:</strong> If data was shared with the beneficiary's consent prior to revocation, that historical sharing cannot be undone, but no future sharing will occur</li>
            </ul>
          </section>

          {/* De-identified Data */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <Users className="w-6 h-6 text-[#FFB800]" />
              <h2 className="text-2xl font-bold text-[#172A3A]">De-identified and Aggregated Data</h2>
            </div>
            <p className="text-gray-700 mb-4">
              We may create de-identified or aggregated data from the information we collect. This data cannot
              reasonably be used to identify any individual. However, we want you to understand:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Re-identification risk:</strong> While we take steps to properly de-identify data according to HIPAA standards, there is always some risk that de-identified data could potentially be re-identified when combined with other data sources</li>
              <li><strong>Use of aggregated data:</strong> We may use aggregated, de-identified data to improve our Service, generate industry insights, or for research purposes</li>
              <li><strong>No sale:</strong> We do not sell de-identified data derived from Medicare beneficiary information</li>
            </ul>
          </section>

          {/* Data Security */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <Lock className="w-6 h-6 text-[#FFB800]" />
              <h2 className="text-2xl font-bold text-[#172A3A]">Data Security</h2>
            </div>
            <p className="text-gray-700 mb-4">
              We implement reasonable administrative, physical, and technical safeguards to protect your data:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Encryption at rest:</strong> All PHI is encrypted using AES-256 encryption</li>
              <li><strong>Encryption in transit:</strong> All data transmission uses TLS 1.2 or higher</li>
              <li><strong>Access controls:</strong> Role-based access controls limit data access to authorized personnel only</li>
              <li><strong>Audit logging:</strong> All access to PHI is logged for compliance and security monitoring</li>
              <li><strong>Secure infrastructure:</strong> Hosted on AWS with SOC 2 and HIPAA-compliant infrastructure</li>
              <li><strong>Credential security:</strong> Developer credentials and API keys are kept confidential and not embedded in publicly accessible code</li>
              <li><strong>Regular assessments:</strong> We conduct regular security assessments and vulnerability testing</li>
              <li><strong>Two-factor authentication:</strong> Available for all user accounts</li>
            </ul>
          </section>

          {/* Data Sharing */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-[#172A3A] mb-4">Information Sharing and Disclosure</h2>
            <p className="text-gray-700 mb-4">
              We do not sell your personal information or PHI. We may share information only in the following circumstances:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>With your explicit consent:</strong> When you specifically authorize us to share information with a named third party</li>
              <li><strong>Service providers:</strong> With trusted third parties who assist in operating our Service, under Business Associate Agreements where applicable. These vendors are contractually required to meet data protection standards matching our legal obligations.</li>
              <li><strong>Legal requirements:</strong> When required by law, subpoena, court order, or legal process</li>
              <li><strong>Business transfers:</strong> In connection with a merger, acquisition, or sale of assets (see "Change of Ownership" section below)</li>
            </ul>
          </section>

          {/* Change of Ownership */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <RefreshCw className="w-6 h-6 text-[#FFB800]" />
              <h2 className="text-2xl font-bold text-[#172A3A]">Change of Ownership</h2>
            </div>
            <p className="text-gray-700">
              If Pro IRP is acquired by or merged with another company, or if substantially all of our assets are
              transferred to another company, your information may be transferred as part of that transaction.
              In such event, we will notify you via email and/or prominent notice on our website prior to any
              transfer of your personal information. You will have the opportunity to opt out of having your
              data transferred to the new entity, and we will honor such requests by deleting your data before
              the transfer occurs. The acquiring entity will be bound by the terms of this Privacy Policy until
              they provide you with a new policy and obtain your consent to any material changes.
            </p>
          </section>

          {/* Data Retention */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <Database className="w-6 h-6 text-[#FFB800]" />
              <h2 className="text-2xl font-bold text-[#172A3A]">Data Retention</h2>
            </div>
            <p className="text-gray-700 mb-4">
              We retain personal information and PHI according to the following guidelines:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Active accounts:</strong> Data is retained as long as your account is active and in good standing</li>
              <li><strong>Medicare data:</strong> Retained only as long as the beneficiary's Blue Button authorization remains valid</li>
              <li><strong>Revoked authorizations:</strong> Medicare data is deleted within 30 days of authorization revocation</li>
              <li><strong>Closed accounts:</strong> Upon account closure, we will delete or de-identify your data within 90 days, unless retention is required for legal or regulatory purposes</li>
              <li><strong>Dormant accounts:</strong> Accounts inactive for more than 24 months may be flagged for closure. We will notify you before taking action on dormant accounts.</li>
              <li><strong>Legal retention:</strong> Some data may be retained longer if required by law, regulation, or to resolve disputes</li>
            </ul>
          </section>

          {/* Breach Notification */}
          <section className="mb-10 bg-red-50 rounded-xl p-6 border border-red-100">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-red-600 mt-1 flex-shrink-0" />
              <div>
                <h2 className="text-xl font-bold text-[#172A3A] mb-2">Data Breach Notification</h2>
                <p className="text-gray-700 mb-4">
                  In the event of a security breach affecting your personal information or PHI, we will:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>Notify affected individuals within 60 days of discovery, as required by HIPAA and the FTC Health Breach Notification Rule</li>
                  <li>Provide details about what information was affected and what steps we are taking</li>
                  <li>Offer guidance on steps you can take to protect yourself</li>
                  <li>Report to HHS and other regulatory bodies as required by law</li>
                  <li>For breaches affecting 500 or more individuals, notify prominent media outlets as required</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Your Rights */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-[#172A3A] mb-4">Your Rights</h2>
            <p className="text-gray-700 mb-4">You have the following rights regarding your information:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Access:</strong> Request a copy of your personal information and any PHI we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request deletion of your information (subject to legal retention requirements)</li>
              <li><strong>Portability:</strong> Receive your data in a commonly used, machine-readable format</li>
              <li><strong>Revoke consent:</strong> Withdraw consent for data processing at any time</li>
              <li><strong>Restrict processing:</strong> Request that we limit how we use your data</li>
              <li><strong>Object:</strong> Object to certain types of data processing</li>
              <li><strong>Complaint:</strong> File a complaint with HHS Office for Civil Rights or your state attorney general</li>
            </ul>
            <p className="text-gray-700 mt-4">
              To exercise any of these rights, contact us at <a href="mailto:fluegel.scott@proirp.com" className="text-[#FFB800] hover:underline">fluegel.scott@proirp.com</a>.
              We will respond to your request within 30 days.
            </p>
          </section>

          {/* Policy Updates */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <Bell className="w-6 h-6 text-[#FFB800]" />
              <h2 className="text-2xl font-bold text-[#172A3A]">Changes to This Privacy Policy</h2>
            </div>
            <p className="text-gray-700 mb-4">
              We may update this Privacy Policy from time to time. When we make changes:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>We will post the updated Privacy Policy on this page and update the "Last Updated" date</li>
              <li>For material changes, we will notify you via email at least 30 days before the changes take effect</li>
              <li>You will have the opportunity to review changes and adjust your settings or opt out before they become effective</li>
              <li>Your continued use of the Service after changes take effect constitutes acceptance of the updated policy</li>
              <li>If you do not agree with changes, you may close your account and request deletion of your data</li>
            </ul>
          </section>

          {/* Children's Privacy */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-[#172A3A] mb-4">Children's Privacy</h2>
            <p className="text-gray-700">
              Our Service is not intended for individuals under 18 years of age. We do not knowingly collect personal
              information from children under 18. If you become aware that a child has provided us with personal
              information, please contact us immediately and we will take steps to delete such information.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-gray-50 rounded-xl p-6 border border-gray-200">
            <div className="flex items-start gap-4">
              <Mail className="w-6 h-6 text-[#172A3A] mt-1 flex-shrink-0" />
              <div>
                <h2 className="text-xl font-bold text-[#172A3A] mb-2">Contact Us</h2>
                <p className="text-gray-700 mb-4">
                  If you have questions about this Privacy Policy, our privacy practices, or wish to exercise
                  your rights, please contact us:
                </p>
                <div className="text-gray-700 space-y-1">
                  <p><strong>Pro IRP</strong></p>
                  <p>Privacy Officer</p>
                  <p>Email: <a href="mailto:fluegel.scott@proirp.com" className="text-[#FFB800] hover:underline">fluegel.scott@proirp.com</a></p>
                  <p>Website: <a href="https://proirp.com" className="text-[#FFB800] hover:underline">proirp.com</a></p>
                </div>
              </div>
            </div>
          </section>

          {/* Footer Links */}
          <div className="mt-10 pt-6 border-t border-gray-200 flex flex-wrap gap-4 justify-center text-sm">
            <Link to="/terms" className="text-[#FFB800] hover:underline">Terms of Service</Link>
            <span className="text-gray-300">|</span>
            <Link to="/" className="text-gray-500 hover:text-[#172A3A]">Home</Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 text-center text-white/60 text-sm">
        <p>&copy; {new Date().getFullYear()} Pro IRP. All rights reserved.</p>
      </footer>
    </div>
  );
}
